// ============================================
// document-mention-list.tsx — R14/R16 @-mention picker
// ============================================
//
// The popup that appears when the user types `@` inside the document
// editor.
//
// R16 enhancements:
//   1. **People support** — the picker now lists both project tasks
//      AND project members, grouped under "Tasks" / "People" labels.
//   2. **Search input** — a dedicated search box sits at the top of
//      the popup. The user can type to filter across both kinds;
//      arrow keys navigate the merged list, Enter selects.
//   3. **Visual differentiation** — task rows show a status badge,
//      person rows show an Avatar + name.
//
// The component is intentionally a plain React component — the
// TipTap Mention extension (in `document-mention-extension.ts`)
// mounts this content inside a tippy.js popover via `ReactRenderer`.
//
// R25 (Bug A — "@ only shows the first 33 tasks") fix:
//   - Task search is now SERVER-SIDE and debounced. The popup queries
//     `GET /api/projects/:projectId/tasks?search=<q>&limit=50&offset=0`
//     so ANY task in the project is reachable by typing, regardless of
//     how many the parent paginated in. Empty query → first 50 tasks in
//     normal view order.
//   - `tasksToMentionItems()` no longer truncates its input. The only
//     remaining cap is `MAX_VISIBLE_TASK_RESULTS`, applied to the
//     FILTERED result set right before render.
//   - People are unchanged (local label-substring filter over the
//     `useProjectMembers` list).

import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { CornerDownRight, Loader2, Search } from 'lucide-react';
import type { ProjectMember, Task, TaskStatus } from '@/lib/schemas';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useDocumentI18n } from './document-i18n';
import {
    MENTION_SEARCH_DEBOUNCE_MS,
    MENTION_TASK_PAGE_SIZE,
    searchMentionTasks,
} from './document-mention-search';
import { cn } from '@/lib/utils';

// ============================================
// TYPES
// ============================================

/** Discriminated union of items the picker can render. */
export type DocumentMentionItem =
    | DocumentMentionTaskItem
    | DocumentMentionPersonItem;

export interface DocumentMentionTaskItem {
    kind: 'task';
    /** Stable task id — encoded into the Mention node attrs. */
    id: string;
    /** Display label — encoded into the Mention node label + chip. */
    label: string;
    /** Status used for the chip badge. */
    status: TaskStatus;
    /**
     * R25: set when the task is a SUBTASK. The picker renders a "↳"
     * hint so subtasks are distinguishable from their parents; the
     * label (and therefore the inserted chip) stays the plain title.
     */
    parentId?: string | null;
}

export interface DocumentMentionPersonItem {
    kind: 'person';
    /** Stable user id — encoded into the Mention node attrs. */
    id: string;
    /** Display name — encoded into the Mention node label + chip. */
    label: string;
    /** Optional avatar URL — chip + picker row render via <Avatar />. */
    avatarUrl?: string | null;
    /** Optional email — used for the avatar fallback initials. */
    email?: string | null;
}

export interface DocumentMentionListProps {
    /** Current items to render. Caller (DocumentView) owns the fetch. */
    items: DocumentMentionItem[];
    /**
     * Command to run on selection. The parent (DocumentView) wraps
     * this with the TipTap chain (delete range + insert mention).
     */
    command: (item: DocumentMentionItem) => void;
    /** Loading state from the upstream useTasks/useProjectMembers query. */
    isLoading?: boolean;
    /**
     * Bug A fix: project id used for the SERVER-SIDE task search. When
     * present, the picker debounces the current search term and calls
     * `GET /api/projects/:projectId/tasks?search=<q>&limit=50&offset=0`
     * so ANY task in the project can be mentioned — not just the ones
     * the parent already paginated into `items`. When absent (e.g.
     * legacy callers / tests) the picker falls back to filtering
     * `items` locally, exactly as it did before.
     */
    projectId?: string;
    /**
     * The live suggestion query — the text typed after `@` in the
     * editor (`SuggestionProps.query`). The popup's own search input
     * normally owns the term (it is auto-focused), but when the editor
     * still holds focus we mirror this value into the input so both
     * entry points drive the same server search.
     */
    query?: string;
}

export interface DocumentMentionListHandle {
    /** Called by the TipTap Mention extension on every keydown inside
     * the editor while the popup is mounted. Returns true when the
     * key was handled (so the editor doesn't insert a character). */
    onKeyDown: (event: { event: KeyboardEvent }) => boolean;
}

// ============================================
// HELPERS
// ============================================

/** Status badge classes — shared with the person-mention popover (Bug B). */
export const TASK_STATUS_BADGE: Record<TaskStatus, string> = {
    TODO: 'bg-slate-500/20 text-slate-300',
    IN_PROGRESS: 'bg-blue-500/20 text-blue-300',
    IN_REVIEW: 'bg-amber-500/20 text-amber-300',
    DONE: 'bg-emerald-500/20 text-emerald-300',
    CANCELLED: 'bg-zinc-500/20 text-zinc-400',
};

/** Human status labels — shared with the person-mention popover (Bug B). */
export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
    TODO: 'To do',
    IN_PROGRESS: 'In progress',
    IN_REVIEW: 'In review',
    DONE: 'Done',
    CANCELLED: 'Cancelled',
};

/**
 * Cap for the PEOPLE list (unchanged R16 behaviour — the member list is
 * small and comes from `useProjectMembers`).
 */
const MAX_ITEMS_PER_KIND = 30;

/**
 * Bug A fix — display cap for the LOCAL fallback list.
 *
 * This is applied to the FILTERED result set (never to the source list)
 * and only when the server search is unavailable (no `projectId`, or the
 * request failed). The normal server path renders whole pages of
 * `MENTION_TASK_PAGE_SIZE` rows appended by the "Load more" button.
 *
 * Before the fix `tasksToMentionItems()` broke out of its loop after 30
 * items, so a project with more than 30 tasks could not have its
 * remaining tasks found by typing — truncation happened before any
 * search.
 */
export const MAX_VISIBLE_TASK_RESULTS = 30;

/**
 * Accumulated server pages for ONE search term.
 *
 * `items` keeps every page fetched so far ("Load more" appends to it),
 * `hasMore` comes from the last response's `{ total, hasMore }`, and
 * `query` identifies the term so a late page for an older term can be
 * discarded.
 */
interface MentionTasksState {
    items: DocumentMentionTaskItem[];
    total: number;
    hasMore: boolean;
    query: string;
}

/**
 * Convert a flat `Task[]` into task picker items — WITHOUT truncating.
 * The cap is applied later, after searching (see
 * `MAX_VISIBLE_TASK_RESULTS`).
 */export function tasksToMentionItems(tasks: Task[]): DocumentMentionTaskItem[] {
    return tasks.map((t) => ({
        kind: 'task',
        id: t.id,
        label: t.title,
        status: t.status,
        parentId: t.parentId ?? null,
    }));
}

/**
 * Convert project members into person picker items.
 */
export function membersToMentionItems(
    members: ProjectMember[]
): DocumentMentionItem[] {
    return members.slice(0, MAX_ITEMS_PER_KIND).map((m) => ({
        kind: 'person' as const,
        id: m.user.id,
        label: m.user.name || m.user.email,
        avatarUrl: m.user.avatarUrl,
        email: m.user.email,
    }));
}

/**
 * Filter items by a case-insensitive substring match on the label.
 */
export function filterMentionItems(
    items: DocumentMentionItem[],
    query: string
): DocumentMentionItem[] {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
}

/**
 * Split a flat mixed-kind list into [tasks, people] arrays.
 */
export function partitionMentionItems(
    items: DocumentMentionItem[]
): { tasks: DocumentMentionTaskItem[]; people: DocumentMentionPersonItem[] } {
    const tasks: DocumentMentionTaskItem[] = [];
    const people: DocumentMentionPersonItem[] = [];
    for (const it of items) {
        if (it.kind === 'task') tasks.push(it);
        else people.push(it);
    }
    return { tasks, people };
}

function initialsFor(label: string, email?: string | null): string {
    const source = label || email || '?';
    return source
        .split(/\s+|@/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
}

// ============================================
// COMPONENT
// ============================================

export const DocumentMentionList = forwardRef<
    DocumentMentionListHandle,
    DocumentMentionListProps
>(function DocumentMentionList(props, ref) {
    const { items, command, isLoading, projectId, query } = props;
    const { t } = useDocumentI18n();

    // R20 round 3 — Bug 5 fix (REAL ROOT CAUSE):
    //
    // The previous picker stayed stuck on the "Loading tasks…"
    // spinner indefinitely when the user wanted to mention a
    // PERSON (or any time `mentionMembersQuery.isLoading` was
    // `true`). The chain was:
    //   1. User types `@` → `onStart` creates the picker with
    //      `isLoading: options.isLoading()` (true).
    //   2. Queries resolve → `mentionLoading` prop flips false.
    //   3. `pushMentionLoadingState(false)` calls
    //      `activeMentionComponent.updateProps({ isLoading: false })`.
    //   4. …except `activeMentionComponent` is a module-scope ref
    //      that gets cleared in `onExit`. If the user opened /
    //      closed / re-opened the picker (e.g. typed `@`,
    //      backspaced, typed `@` again), the ref pointed at the
    //      DESTROYED first component while the live one had no
    //      updateProps call. The new component stayed on its
    //      initial `isLoading: true` snapshot forever.
    //
    // The fix is to never gate the picker UI on `isLoading` alone.
    // We show whatever `items` are available (stale data is
    // better than an infinite spinner), and we add a SAFETY NET:
    // after 1500 ms we flip an internal `forceReady` flag so even
    // a stuck query can't block the picker forever. The
    // `isLoading` prop is now used ONLY for the small spinner
    // badge next to the section title — the body always shows
    // items when present.

    // R16: dedicated search input at the top of the popup.
    //
    // Bug A (R25) update: this input is now the driver for the
    // SERVER-SIDE task search — every (debounced) change re-queries
    // `/projects/:projectId/tasks?search=…` so tasks beyond the parent's
    // loaded page are reachable. People are still filtered locally from
    // the member list the extension hands us.
    const [search, setSearch] = useState(query ?? '');
    const searchRef = useRef<HTMLInputElement | null>(null);

    // Mirror the live `@<query>` from the editor into the popup input.
    // The input is auto-focused, so in practice the editor query stays
    // empty and the input is the source of truth; this sync matters when
    // the user typed after the trigger while the editor still had focus.
    const externalQueryRef = useRef(query ?? '');
    useEffect(() => {
        const incoming = query ?? '';
        if (incoming === externalQueryRef.current) return;
        externalQueryRef.current = incoming;
        setSearch(incoming);
    }, [query]);

    // The single term that drives BOTH the server-side task search and
    // the local people / fallback filter.
    const effectiveQuery = search.trim();

    // ------------------------------------------------------------------
    // Bug A fix — debounced SERVER-SIDE task search with "Load more".
    //
    // Before this, the picker could only ever see the tasks the parent
    // had already loaded (`useTasks` first page = 40) truncated to the
    // first 30 BEFORE any filtering — a project with more tasks than
    // that could not have the remaining ones found by typing. The picker
    // now asks the backend directly:
    //
    //   GET /api/projects/:projectId/tasks?search=<q>&limit=30&offset=0
    //
    // - Typing always searches the WHOLE project (server-side `search`),
    //   never just the rows currently on screen.
    // - 30 rows are rendered per page; `hasMore` from the response drives
    //   a "Load more" button that fetches the next page and APPENDS it.
    // - Changing the term clears the accumulated pages and restarts at
    //   offset 0.
    //
    // Stale-response handling: the first-page effect owns a sequence
    // number AND an AbortController (cleanup aborts + bumps the
    // sequence). "Load more" uses a second sequence + controller that are
    // invalidated whenever the term changes, so a late page can never be
    // appended to a different query's results.
    // ------------------------------------------------------------------
    const [tasksState, setTasksState] = useState<MentionTasksState | null>(null);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [tasksLoadingMore, setTasksLoadingMore] = useState(false);
    const [remoteError, setRemoteError] = useState(false);
    const requestSeqRef = useRef(0);
    const loadMoreSeqRef = useRef(0);
    const loadMoreControllerRef = useRef<AbortController | null>(null);
    // Latest accumulated state, readable from the (stable) click handler.
    const tasksStateRef = useRef<MentionTasksState | null>(null);
    tasksStateRef.current = tasksState;

    // Abort an in-flight "Load more" — used on unmount and term change.
    const cancelLoadMore = useCallback(() => {
        loadMoreSeqRef.current += 1;
        loadMoreControllerRef.current?.abort();
        loadMoreControllerRef.current = null;
    }, []);

    useEffect(() => {
        if (!projectId) return;
        // New term (or new project): reset pagination — drop every
        // appended page and invalidate an in-flight "Load more" so a
        // stale page can't land in the new result set.
        cancelLoadMore();
        setTasksLoadingMore(false);
        setTasksState(null);

        const seq = ++requestSeqRef.current;
        const controller = new AbortController();
        const timer = window.setTimeout(() => {
            setTasksLoading(true);
            searchMentionTasks(projectId, effectiveQuery, {
                offset: 0,
                limit: MENTION_TASK_PAGE_SIZE,
                signal: controller.signal,
            })
                .then((page) => {
                    if (seq !== requestSeqRef.current) return; // stale — ignore
                    setTasksState({
                        items: tasksToMentionItems(page.tasks),
                        total: page.total,
                        hasMore: page.hasMore,
                        query: effectiveQuery,
                    });
                    setTasksLoading(false);
                    setRemoteError(false);
                })
                .catch((err: unknown) => {
                    if (seq !== requestSeqRef.current) return; // stale — ignore
                    if (err instanceof Error && err.name === 'AbortError') return;
                    setTasksLoading(false);
                    setRemoteError(true);
                    // Fall back to the parent-provided list so the picker
                    // keeps working when the search endpoint is down.
                    setTasksState(null);
                });
        }, MENTION_SEARCH_DEBOUNCE_MS);
        return () => {
            window.clearTimeout(timer);
            controller.abort();
            if (requestSeqRef.current === seq) requestSeqRef.current += 1;
        };
    }, [projectId, effectiveQuery, cancelLoadMore]);

    // Abort any pending append when the picker closes.
    useEffect(() => cancelLoadMore, [cancelLoadMore]);

    /**
     * "Load more" — fetch the NEXT page for the current term and append
     * it. Never inserts a mention; merely grows the visible list.
     */
    const handleLoadMore = useCallback(() => {
        const current = tasksStateRef.current;
        if (!projectId || !current || !current.hasMore) return;
        if (loadMoreControllerRef.current) return; // already in flight

        const query = current.query;
        const offset = current.items.length;
        const seq = ++loadMoreSeqRef.current;
        const controller = new AbortController();
        loadMoreControllerRef.current = controller;
        setTasksLoadingMore(true);

        searchMentionTasks(projectId, query, {
            offset,
            limit: MENTION_TASK_PAGE_SIZE,
            signal: controller.signal,
        })
            .then((page) => {
                if (seq !== loadMoreSeqRef.current) return; // stale — ignore
                loadMoreControllerRef.current = null;
                setTasksState((prev) => {
                    if (!prev || prev.query !== query) return prev;
                    // De-dupe by id: rows can shift between pages while
                    // other people edit the project.
                    const seen = new Set(prev.items.map((i) => i.id));
                    const appended = tasksToMentionItems(page.tasks).filter(
                        (i) => !seen.has(i.id)
                    );
                    return {
                        items: [...prev.items, ...appended],
                        total: page.total,
                        hasMore: page.hasMore && appended.length > 0,
                        query,
                    };
                });
                setTasksLoadingMore(false);
                setRemoteError(false);
            })
            .catch((err: unknown) => {
                if (seq !== loadMoreSeqRef.current) return; // stale — ignore
                loadMoreControllerRef.current = null;
                if (err instanceof Error && err.name === 'AbortError') return;
                setTasksLoadingMore(false);
                setRemoteError(true);
            });
    }, [projectId]);

    const { tasks: propTaskItems, people: propPeopleItems } = useMemo(
        () => partitionMentionItems(items),
        [items]
    );

    // People keep the exact R16 behaviour: the member list is already
    // fully loaded and is filtered locally by label substring.
    const people = useMemo(
        () =>
            filterMentionItems(
                propPeopleItems,
                effectiveQuery
            ) as DocumentMentionPersonItem[],
        [propPeopleItems, effectiveQuery]
    );

    // Tasks: server results when available. Those are already filtered
    // by the backend (title OR description), so the local title-only
    // filter must NOT be re-applied — it would drop description matches
    // again. In fallback mode (no projectId, or the request failed) we
    // filter the parent-provided list locally, exactly as before.
    //
    // Every accumulated server page is rendered: the 30-row page size is
    // enforced by the request (`MENTION_TASK_PAGE_SIZE`), and "Load more"
    // appends further pages on top — that is exactly why there is no
    // slice() on `remote.items` here.
    const tasks = useMemo(() => {
        const remote = tasksState;
        if (remote) {
            return remote.items;
        }
        return (
            filterMentionItems(
                propTaskItems,
                effectiveQuery
            ) as DocumentMentionTaskItem[]
        ).slice(0, MAX_VISIBLE_TASK_RESULTS);
    }, [tasksState, propTaskItems, effectiveQuery]);

    /** Show "Load more" only for the CURRENT term's server result set. */
    const showLoadMore =
        !!projectId &&
        !remoteError &&
        !!tasksState &&
        tasksState.query === effectiveQuery &&
        tasksState.hasMore;

    // Navigation order mirrors the visual order: tasks → "Load more" →
    // people. The `null` entry is the Load more button: arrow keys can
    // reach it and Enter activates it instead of inserting a mention.
    const navEntries = useMemo<Array<DocumentMentionItem | null>>(
        () => [
            ...tasks,
            ...(showLoadMore ? [null] : []),
            ...people,
        ],
        [tasks, showLoadMore, people]
    );

    /** `${kind}:${id}` → index in `navEntries` (rows are keyed by kind+id). */
    const navIndexByKey = useMemo(() => {
        const map = new Map<string, number>();
        navEntries.forEach((entry, index) => {
            if (entry) map.set(`${entry.kind}:${entry.id}`, index);
        });
        return map;
    }, [navEntries]);

    const itemCount = tasks.length + people.length;

    // True while the server list for the CURRENT term isn't in yet
    // (debouncing or in flight). Drives the section spinner so the user
    // sees that more results may still arrive.
    const remotePending =
        !!projectId &&
        !remoteError &&
        (tasksLoading || tasksState?.query !== effectiveQuery);

    const [selectedIndex, setSelectedIndex] = useState(0);

    // Clamp the selection to the current navigation length (items plus
    // the optional "Load more" entry).
    useEffect(() => {
        if (selectedIndex >= navEntries.length) {
            setSelectedIndex(Math.max(0, navEntries.length - 1));
        }
    }, [navEntries.length, selectedIndex]);

    // R20 round 3 — Bug 5 safety net: if `isLoading` is true and
    // no items are available after 1500 ms, flip `forceReady` so
    // the picker shows the "no matching tasks" empty state
    // instead of an infinite spinner. This handles the case
    // where `mentionMembersQuery` (or any other upstream query)
    // is slow / stuck and the user would otherwise be staring at
    // a spinner forever.
    const [forceReady, setForceReady] = useState(false);
    useEffect(() => {
        if (!(isLoading || remotePending) || itemCount > 0) {
            setForceReady(false);
            return;
        }
        const id = window.setTimeout(() => setForceReady(true), 1500);
        return () => window.clearTimeout(id);
    }, [isLoading, remotePending, itemCount]);

    const selectIndex = useCallback(
        (index: number) => {
            // The last entry may be the "Load more" button (null) —
            // activating it pages the task list instead of inserting a
            // mention.
            if (index === navEntries.length - 1 && showLoadMore) {
                handleLoadMore();
                return;
            }
            const item = navEntries[index];
            if (!item) return;
            command(item);
        },
        [navEntries, showLoadMore, handleLoadMore, command]
    );

    // Focus the search input when the popup mounts so the user can
    // immediately type to filter. We do this in an effect (not on
    // mount via autoFocus) so the tippy.js popover has fully
    // mounted before we grab focus.
    useEffect(() => {
        const id = requestAnimationFrame(() => {
            searchRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, []);

    // R16: arrow / Enter / Tab handling. Centralised here so the
    // picker-imperative-handle AND the inline search input can both
    // dispatch to the same handler.
    //
    // R25: the arrow ring now includes the "Load more" entry so it is
    // keyboard-reachable; Enter on it loads the next page (it never
    // inserts a mention).
    const navLength = navEntries.length;
    const handleNavKey = useCallback(
        (event: KeyboardEvent | React.KeyboardEvent): boolean => {
            if (event.key === 'ArrowUp') {
                setSelectedIndex((prev) =>
                    navLength === 0
                        ? 0
                        : (prev - 1 + navLength) % navLength
                );
                return true;
            }
            if (event.key === 'ArrowDown') {
                setSelectedIndex((prev) =>
                    navLength === 0 ? 0 : (prev + 1) % navLength
                );
                return true;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                selectIndex(selectedIndex);
                return true;
            }
            return false;
        },
        [navLength, selectedIndex, selectIndex]
    );

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            const target = event.target as HTMLElement | null;
            const isInSearch = target?.tagName === 'INPUT';

            // Navigation keys always go to the picker regardless of
            // where focus is (search input, list, etc.).
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                return handleNavKey(event);
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                return handleNavKey(event);
            }
            // Printable characters typed outside the search input
            // are swallowed — the user is in the editor body but
            // didn't intend to type into the document while the
            // picker is up. (Inside the input they're handled by
            // the input's onChange + the picker re-filters.)
            if (
                !isInSearch &&
                event.key.length === 1 &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.altKey
            ) {
                return true;
            }
            return false;
        },
    }));

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    // R20 round 3 — Bug 5: only show the full-screen "Loading…"
    // spinner when `isLoading` AND we have no items AND the safety
    // timer hasn't fired yet. Otherwise, render the items / empty
    // state so the picker is never stuck forever. We also show a
    // small inline spinner next to the section title when items
    // are present but the upstream query is still fetching (so
    // the user knows more items may appear).
    const showLoadingOnly =
        (isLoading || remotePending) && itemCount === 0 && !forceReady;
    const showInlineSpinner = (isLoading || remotePending) && itemCount > 0;

    // R26 repair: this branch used to `return` a spinner-only tree, which
    // UNMOUNTED the search input whenever a query was in flight. The input
    // lost focus mid-typing and every following keystroke went to the
    // document body, so the typed query was truncated and real matches
    // were never found ("I search for a specific one and it doesn't
    // work"). The loading row is now rendered INSIDE the popup (see the
    // JSX below) so the input stays mounted and keeps focus.

    const hasNoSourceItems = itemCount === 0 && effectiveQuery.length === 0;
    const hasNoFilteredItems = itemCount === 0 && effectiveQuery.length > 0;

    return (
        <div
            className="min-w-[280px] overflow-hidden rounded-md border border-border bg-surface shadow-lg"
            data-testid="document-mention-list"
        >
            {/* R16: dedicated search bar */}
            <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
                <Search
                    className="h-3.5 w-3.5 shrink-0 text-foreground-muted"
                    aria-hidden="true"
                />
                <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        // Re-route Enter + arrows into our picker
                        // handler so the search input doesn't fight
                        // the picker for them.
                        if (handleNavKey(e)) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    placeholder={t('document.mention.searchPlaceholder')}
                    aria-label={t('document.mention.searchAriaLabel')}
                    data-testid="document-mention-search-input"
                    className="w-full bg-transparent text-xs text-foreground placeholder:text-foreground-muted focus:outline-none"
                />
            </div>

            {/* Bug A: surface a failed server search instead of
                silently keeping stale/partial results. The picker keeps
                working off the parent-provided list in that case. */}
            {remoteError ? (
                <div
                    className="border-b border-border px-3 py-1 text-[10px] text-amber-400/80"
                    data-testid="document-mention-search-error"
                >
                    {t('document.mention.searchUnavailable')}
                </div>
            ) : null}

            {showLoadingOnly ? (
                <div
                    className="flex items-center gap-2 px-3 py-2 text-xs text-foreground-muted"
                    data-testid="document-mention-loading"
                >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('document.mention.loading')}
                </div>
            ) : hasNoSourceItems ? (
                <div
                    className="px-3 py-2 text-xs text-foreground-muted"
                    data-testid="document-mention-empty"
                >
                    {t('document.mention.empty')}
                </div>
            ) : hasNoFilteredItems ? (
                <div
                    className="px-3 py-2 text-xs text-foreground-muted"
                    data-testid="document-mention-empty-search"
                >
                    {t('document.mention.emptyAfterSearch', { query: effectiveQuery })}
                </div>
            ) : (
                <div
                    role="listbox"
                    aria-label={t('document.mention.pickerAriaLabel')}
                    className="max-h-64 overflow-auto p-1"
                >
                    {tasks.length > 0 ? (
                        <MentionSection
                            label={t('document.mention.section.tasks')}
                            count={tasks.length}
                            kind="tasks"
                            showSpinner={showInlineSpinner}
                        >
                            {tasks.map((item) => {
                                const index =
                                    navIndexByKey.get(`task:${item.id}`) ?? -1;
                                return (
                                    <MentionRow
                                        key={`task:${item.id}`}
                                        item={item}
                                        selected={index === selectedIndex}
                                        onSelect={() => selectIndex(index)}
                                        onHover={() => setSelectedIndex(index)}
                                        taskLabel={t('document.mention.task')}
                                    />
                                );
                            })}
                            {/* R25 (Bug A refinement): the server page
                                has more matches than the 30 shown —
                                append the next page. It is part of the
                                arrow-key ring (Enter activates it) and
                                never inserts a mention itself. */}
                            {showLoadMore ? (
                                <button
                                    type="button"
                                    data-testid="document-mention-load-more"
                                    // Keep focus in the search input, like
                                    // the rows do.
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleLoadMore();
                                    }}
                                    onMouseEnter={() =>
                                        setSelectedIndex(navEntries.length - 1)
                                    }
                                    disabled={tasksLoadingMore}
                                    className={cn(
                                        'flex w-full items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-[11px] font-medium transition-colors',
                                        'text-foreground-muted hover:bg-white/5 hover:text-foreground',
                                        'disabled:cursor-default disabled:opacity-60',
                                        selectedIndex === navEntries.length - 1 &&
                                            'bg-primary/20 text-foreground'
                                    )}
                                >
                                    {tasksLoadingMore ? (
                                        <Loader2
                                            className="h-3 w-3 animate-spin"
                                            aria-hidden="true"
                                        />
                                    ) : null}
                                    {t('document.mention.loadMore')}
                                </button>
                            ) : null}
                        </MentionSection>
                    ) : (
                        <MentionSection
                            label={t('document.mention.section.tasks')}
                            count={0}
                            kind="tasks"
                            showSpinner={showInlineSpinner}
                        >
                            {/* No tasks yet — either still loading or
                                no tasks in this project. Either way,
                                the spinner in the label tells the
                                user we're waiting. */}
                        </MentionSection>
                    )}
                    {people.length > 0 ? (
                        <MentionSection
                            label={t('document.mention.section.people')}
                            count={people.length}
                            kind="people"
                        >
                            {people.map((item) => {
                                const index =
                                    navIndexByKey.get(`person:${item.id}`) ?? -1;
                                return (
                                    <MentionRow
                                        key={`person:${item.id}`}
                                        item={item}
                                        selected={index === selectedIndex}
                                        onSelect={() => selectIndex(index)}
                                        onHover={() => setSelectedIndex(index)}
                                        personLabel={t('document.mention.person')}
                                    />
                                );
                            })}
                        </MentionSection>
                    ) : (
                        <MentionSection
                            label={t('document.mention.section.people')}
                            count={0}
                            kind="people"
                            showSpinner={showInlineSpinner}
                        >
                            {/* No people yet — same as above. */}
                        </MentionSection>
                    )}
                </div>
            )}
        </div>
    );
});

// ============================================
// SECTION LABEL
// ============================================

interface MentionSectionProps {
    label: string;
    count: number;
    kind: 'tasks' | 'people';
    children?: React.ReactNode;
    showSpinner?: boolean;
}

function MentionSection({ label, count, kind, children, showSpinner }: MentionSectionProps) {
    return (
        <div
            className="mt-1 first:mt-0"
            data-testid={`document-mention-section-${kind}`}
        >
            <div className="flex items-center justify-between px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">
                <span className="flex items-center gap-1.5">
                    {label}
                    {showSpinner && (
                        // R20 round 3: show a tiny spinner next
                        // to the section label while the
                        // upstream query is still in flight.
                        // The bug report said person mentions
                        // were infinitely loading — surfacing
                        // the in-flight state lets the user
                        // know we're fetching more names.
                        <Loader2
                            className="h-2.5 w-2.5 animate-spin text-foreground-muted/60"
                            aria-hidden="true"
                        />
                    )}
                </span>
                <span className="text-foreground-muted/60">{count}</span>
            </div>
            <div className="space-y-0.5">{children}</div>
        </div>
    );
}

// ============================================
// ROW
// ============================================

interface MentionRowProps {
    item: DocumentMentionItem;
    selected: boolean;
    onSelect: () => void;
    onHover: () => void;
    taskLabel?: string;
    personLabel?: string;
}

function MentionRow({
    item,
    selected,
    onSelect,
    onHover,
    taskLabel,
    personLabel,
}: MentionRowProps) {
    if (item.kind === 'task') {
        return (
            <button
                type="button"
                role="option"
                aria-selected={selected}
                data-testid="document-mention-item"
                data-kind="task"
                data-task-id={item.id}
                onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect();
                }}
                onMouseEnter={onHover}
                className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors',
                    selected
                        ? 'bg-primary/20 text-foreground'
                        : 'text-foreground-muted hover:bg-white/5 hover:text-foreground'
                )}
            >
                {taskLabel ? (
                    <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary/80">
                        {taskLabel}
                    </span>
                ) : null}
                {/* R25: subtasks carry a "↳" hint (the inserted chip
                    keeps the plain title as its label). */}
                {item.parentId ? (
                    <CornerDownRight
                        className="h-3 w-3 shrink-0 text-foreground-muted/70"
                        aria-hidden="true"
                    />
                ) : null}
                <span className="flex-1 truncate">{item.label}</span>
                <span
                    className={cn(
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                        TASK_STATUS_BADGE[item.status]
                    )}
                >
                    {TASK_STATUS_LABEL[item.status]}
                </span>
            </button>
        );
    }

    // person row
    return (
        <button
            type="button"
            role="option"
            aria-selected={selected}
            data-testid="document-mention-item"
            data-kind="person"
            data-person-id={item.id}
            onMouseDown={(e) => {
                e.preventDefault();
                onSelect();
            }}
            onMouseEnter={onHover}
            className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors',
                selected
                    ? 'bg-primary/20 text-foreground'
                    : 'text-foreground-muted hover:bg-white/5 hover:text-foreground'
            )}
        >
            <Avatar className="h-6 w-6 shrink-0">
                {item.avatarUrl ? (
                    <AvatarImage src={item.avatarUrl} alt={item.label} />
                ) : null}
                <AvatarFallback className="text-[10px] font-semibold">
                    {initialsFor(item.label, item.email)}
                </AvatarFallback>
            </Avatar>
            <span className="flex flex-1 items-center gap-2 truncate">
                {personLabel ? (
                    <span className="shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                        {personLabel}
                    </span>
                ) : null}
                <span className="truncate">{item.label}</span>
            </span>
        </button>
    );
}
